import { useState, useCallback, useMemo, type ReactNode } from 'react'
import { ReadOnlyContext, type ReadOnlyReason } from './ReadOnlyContextDef'

interface ReadOnlyProviderProps {
  /** Reasons that are active from mount time (e.g. ['public'] for public routes) */
  initialReasons?: ReadOnlyReason[]
  children: ReactNode
}

export function ReadOnlyProvider({ initialReasons, children }: ReadOnlyProviderProps) {
  const [reasons, setReasons] = useState<Set<ReadOnlyReason>>(() => new Set(initialReasons ?? []))

  const activateReadOnly = useCallback((reason: ReadOnlyReason): void => {
    setReasons((prev) => {
      if (prev.has(reason)) return prev
      const next = new Set(prev)
      next.add(reason)
      return next
    })
  }, [])

  const deactivateReadOnly = useCallback((reason: ReadOnlyReason): void => {
    setReasons((prev) => {
      if (!prev.has(reason)) return prev
      const next = new Set(prev)
      next.delete(reason)
      return next
    })
  }, [])

  const value = useMemo(() => {
    const isReadOnly = reasons.size > 0
    const firstReason = reasons.size > 0 ? reasons.values().next().value : null
    return {
      isReadOnly,
      readOnlyReason: firstReason as ReadOnlyReason | null,
      readOnlyReasons: reasons,
      activateReadOnly,
      deactivateReadOnly,
    }
  }, [reasons, activateReadOnly, deactivateReadOnly])

  return <ReadOnlyContext.Provider value={value}>{children}</ReadOnlyContext.Provider>
}
